import { useState, useCallback, useEffect, useRef } from 'react'
import type { ChecklistState, ChecklistProgress } from '@/types/checklist'
import { fetchChecklist, saveChecklist } from '@/lib/checklist-api'
import {
  getChecklistProgress,
  addTopic as libAddTopic,
  updateTopic as libUpdateTopic,
  removeTopic as libRemoveTopic,
  moveTopic as libMoveTopic,
  addItem as libAddItem,
  updateItem as libUpdateItem,
  removeItem as libRemoveItem,
  moveItem as libMoveItem,
  addSubitem as libAddSubitem,
  updateSubitem as libUpdateSubitem,
  removeSubitem as libRemoveSubitem,
  moveSubitem as libMoveSubitem,
} from '@/lib/checklist'

export interface ChecklistActions {
  addTopic: () => void
  updateTopic: (topicId: string, updates: { title?: string }) => void
  removeTopic: (topicId: string) => void
  moveTopic: (topicId: string, direction: 'up' | 'down') => void
  addItem: (topicId: string) => void
  updateItem: (
    itemId: string,
    updates: { text?: string; completed?: boolean; dueDate?: string; note?: string }
  ) => void
  removeItem: (itemId: string) => void
  moveItem: (topicId: string, itemId: string, direction: 'up' | 'down') => void
  addSubitem: (itemId: string) => void
  updateSubitem: (
    subitemId: string,
    updates: { text?: string; completed?: boolean }
  ) => void
  removeSubitem: (subitemId: string) => void
  moveSubitem: (
    itemId: string,
    subitemId: string,
    direction: 'up' | 'down'
  ) => void
}

const SAVE_DEBOUNCE_MS = 800

export function useChecklist(): {
  state: ChecklistState
  progress: ChecklistProgress
  actions: ChecklistActions
  isLoading: boolean
  isSaving: boolean
  error: string | null
} {
  const [state, setState] = useState<ChecklistState>({ topics: [] })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSaveRef = useRef<ChecklistState | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    setIsLoading(true)
    fetchChecklist()
      .then((data) => {
        if (!cancelled) setState(data)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar checklist')
          setState({ topics: [] })
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const persistState = useCallback((nextState: ChecklistState) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    pendingSaveRef.current = nextState
    saveTimeoutRef.current = setTimeout(async () => {
      saveTimeoutRef.current = null
      const toSave = pendingSaveRef.current
      pendingSaveRef.current = null
      if (!toSave) return
      setIsSaving(true)
      try {
        const saved = await saveChecklist(toSave)
        setState(saved)
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao salvar checklist')
      } finally {
        setIsSaving(false)
      }
    }, SAVE_DEBOUNCE_MS)
  }, [])

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  const progress = getChecklistProgress(state)

  const actions: ChecklistActions = {
    addTopic: useCallback(() => {
      setState((s: ChecklistState) => {
        const next = libAddTopic(s)
        persistState(next)
        return next
      })
    }, [persistState]),
    updateTopic: useCallback(
      (topicId: string, updates: { title?: string }) => {
        setState((s: ChecklistState) => {
          const next = libUpdateTopic(s, topicId, updates)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    removeTopic: useCallback(
      (topicId: string) => {
        setState((s: ChecklistState) => {
          const next = libRemoveTopic(s, topicId)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    moveTopic: useCallback(
      (topicId: string, direction: 'up' | 'down') => {
        setState((s: ChecklistState) => {
          const next = libMoveTopic(s, topicId, direction)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    addItem: useCallback(
      (topicId: string) => {
        setState((s: ChecklistState) => {
          const next = libAddItem(s, topicId)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    updateItem: useCallback(
      (
        itemId: string,
        updates: {
          text?: string
          completed?: boolean
          dueDate?: string
          note?: string
        }
      ) => {
        setState((s: ChecklistState) => {
          const next = libUpdateItem(s, itemId, updates)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    removeItem: useCallback(
      (itemId: string) => {
        setState((s: ChecklistState) => {
          const next = libRemoveItem(s, itemId)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    moveItem: useCallback(
      (topicId: string, itemId: string, direction: 'up' | 'down') => {
        setState((s: ChecklistState) => {
          const next = libMoveItem(s, topicId, itemId, direction)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    addSubitem: useCallback(
      (itemId: string) => {
        setState((s: ChecklistState) => {
          const next = libAddSubitem(s, itemId)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    updateSubitem: useCallback(
      (
        subitemId: string,
        updates: { text?: string; completed?: boolean }
      ) => {
        setState((s: ChecklistState) => {
          const next = libUpdateSubitem(s, subitemId, updates)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    removeSubitem: useCallback(
      (subitemId: string) => {
        setState((s: ChecklistState) => {
          const next = libRemoveSubitem(s, subitemId)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
    moveSubitem: useCallback(
      (itemId: string, subitemId: string, direction: 'up' | 'down') => {
        setState((s: ChecklistState) => {
          const next = libMoveSubitem(s, itemId, subitemId, direction)
          persistState(next)
          return next
        })
      },
      [persistState]
    ),
  }

  return { state, progress, actions, isLoading, isSaving, error }
}
